import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { Button, Modal, ProductCard, Typography, useToast } from '@/primer/components';
import { PageColumn, PageLoadingState, ScreenCard } from '@/components/layout';
import { useInvite, useDeclineInvite, useAuth } from '@/api';
import { useAcceptInvite, type PostAcceptDestination } from '@/hooks/useAcceptInvite';
import { ACTIVE_CAMPAIGN_USER_STATES } from '@/types';
import { useMixpanel } from '@/contexts';
import { trackFunnelEvent, CREATOR_FUNNEL_EVENTS } from '@/utils';

/** Format a platform string ("INSTAGRAM") to a human-readable label ("Instagram"). */
function formatPlatform(platform: string): string {
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

export function MagicLink() {
  const { token } = useSearch({ from: '/_public/invite' });
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { data: invite, isLoading, error } = useInvite(token || '');
  const { linkAndAccept, isAccepting } = useAcceptInvite(token || '');
  const { mixpanel } = useMixpanel();
  const hasSentViewedEvent = useRef(false);

  const hasAlreadyAccepted =
    invite?.campaignUserState != null &&
    ACTIVE_CAMPAIGN_USER_STATES.some((state) => state === invite.campaignUserState);

  useEffect(() => {
    if (
      hasSentViewedEvent.current ||
      !invite ||
      hasAlreadyAccepted ||
      invite.inviteStatus === 'DECLINED'
    )
      return;
    // ref stays false on a transient Mixpanel SDK failure so it can be retried
    hasSentViewedEvent.current = trackFunnelEvent(
      mixpanel,
      CREATOR_FUNNEL_EVENTS.INVITE_VIEWED,
      {
        campaign_id: String(invite.campaignId),
        campaign_name: invite.campaignName,
      },
      [invite.campaignName]
    );
  }, [invite, hasAlreadyAccepted, mixpanel]);

  const declineMutation = useDeclineInvite();
  const { showToast } = useToast();
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);

  // Once an invite is declined, the creator should leave the invite flow entirely.
  // Prefer closing the tab (works when it was opened via window.open); browsers
  // block window.close() for directly-opened tabs, so fall back to the landing page.
  const leaveAfterDecline = useCallback(() => {
    window.close();
    navigate({ to: '/' });
  }, [navigate]);

  // Resolve terminal invite states before rendering the proposal.
  useEffect(() => {
    if (invite?.inviteStatus === 'DECLINED') {
      leaveAfterDecline();
      return;
    }

    if (!hasAlreadyAccepted || isAuthLoading) return;

    if (isAuthenticated) {
      navigate({ to: '/dashboard', replace: true });
    } else {
      navigate({ to: '/', replace: true });
    }
  }, [
    hasAlreadyAccepted,
    invite?.inviteStatus,
    isAuthLoading,
    isAuthenticated,
    leaveAfterDecline,
    navigate,
  ]);

  const handleAccept = async () => {
    if (!invite || !token) return;

    if (!isAuthenticated) {
      navigate({ to: '/invite/auth', search: { token } });
      return;
    }

    let destination: PostAcceptDestination;
    try {
      destination = await linkAndAccept();
    } catch (err) {
      console.error('Failed to accept invite:', err instanceof Error ? err.message : err);
      showToast({
        variant: 'error',
        message: "Something went wrong. We couldn't complete your request. Please try again.",
      });
      return;
    }

    navigate({ ...destination, replace: true });
  };

  const handleNoThanks = () => {
    setShowDeclineConfirmation(true);
  };

  const handleConfirmDecline = async () => {
    if (declineMutation.isPending) return;

    try {
      await declineMutation.mutateAsync({ token: token || '' });
    } catch (err) {
      console.error('Failed to decline invite:', err);
      // Keep the confirmation modal open and surface the failure to the user.
      showToast({
        variant: 'error',
        message: "Couldn't decline the invitation. Please try again.",
      });
      return;
    }

    const declinedCampaignId = invite?.campaignId;
    const declinedCampaignName = invite?.campaignName;

    trackFunnelEvent(
      mixpanel,
      CREATOR_FUNNEL_EVENTS.INVITE_DECLINED,
      {
        campaign_id: String(declinedCampaignId),
        campaign_name: declinedCampaignName ?? '',
      },
      [declinedCampaignId, declinedCampaignName]
    );

    leaveAfterDecline();
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isLoading || isAuthLoading || hasAlreadyAccepted) {
    return <PageLoadingState size="large" />;
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (error || !invite) {
    return (
      <PageColumn className="flex flex-col flex-1">
        <ScreenCard shadow="second">
          <div className="p-8 flex flex-col items-center gap-4 text-center">
            <Typography variant="header" size="h2" color="error">
              Invalid Invitation
            </Typography>
            <Typography variant="body" size="medium" color="bodylighter">
              This invitation link is invalid or has expired.
            </Typography>
            <Button onClick={() => navigate({ to: '/' })}>Go to Home</Button>
          </div>
        </ScreenCard>
      </PageColumn>
    );
  }

  // ── Declined ───────────────────────────────────────────────────────────────
  // A declined invite never shows the proposal again — the effect above closes
  // the tab / redirects to login. Render a spinner while that happens.
  if (invite.inviteStatus === 'DECLINED') {
    return <PageLoadingState size="large" />;
  }

  const firstProduct = invite.products[0];

  // ── Brand avatar ─────────────────────────────────────────────────────────────
  // TODO: Re-enable when a brand logo URL field is available from the API.
  // The avatar circle overlaps the ScreenCard using Primer spacing tokens.
  // See git history for the full implementation.
  const avatarSrc: string | undefined = undefined;

  return (
    <PageColumn className="flex flex-col flex-1">
      {/* ── Brand avatar (commented out until API provides a brand logo URL) ──
           When re-enabling: restore the block below and set avatarSrc correctly.
      {avatarSrc && (
        <div className="flex justify-center -mb-[1.875rem] relative z-10">
          <div
            className="w-[calc(var(--spacing-10)+var(--spacing-5))] h-[calc(var(--spacing-10)+var(--spacing-5))] rounded-rounded bg-surface shadow-(--shadow-top-light-first-floor) overflow-hidden flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <img
              src={avatarSrc}
              alt={firstProduct?.brand}
              className="w-full h-full object-contain p-3"
            />
          </div>
        </div>
      )}
      ── */}

      <>
        <ScreenCard shadow="second">
          <div className={`px-6 pb-8 ${avatarSrc ? 'pt-6' : 'pt-8'}`}>
            {/* ── Headline ──────────────────────────────────────────────────────── */}
            <Typography variant="header" size="h1" align="center" as="h1">
              You've been invited to an exclusive brand partnership.
            </Typography>

            {/* ── Product card ──────────────────────────────────────────────────── */}
            {firstProduct && (
              <div className="mt-6">
                <ProductCard
                  imageUrl={firstProduct.imageUrl}
                  imageAlt={firstProduct.name}
                  brandName={firstProduct.brand}
                  productName={firstProduct.name}
                  shadow="first"
                  as="article"
                />
              </div>
            )}

            {/* ── Proposal section ──────────────────────────────────────────────── */}
            <div className="mt-6">
              <Typography variant="header" size="h2" align="center" as="h2">
                The proposal 👇
              </Typography>

              <ul className="mt-4 flex flex-col gap-3 list-none p-0">
                {/* Ship for free */}
                <li className="flex gap-2 items-start">
                  <Typography variant="body" size="medium" as="span">
                    &bull;
                  </Typography>
                  <Typography variant="body" size="medium" as="span">
                    We'll ship this product to you{' '}
                    <Typography
                      variant="body"
                      size="medium"
                      as="strong"
                      decoration={{ bold: true, underline: true }}
                    >
                      for free
                    </Typography>
                  </Typography>
                </li>

                {/* Social platform + username */}
                {invite.username && invite.platform && (
                  <li className="flex gap-2 items-start">
                    <Typography variant="body" size="medium" as="span">
                      &bull;
                    </Typography>
                    <Typography variant="body" size="medium" as="span">
                      You'll post a review of this product on this {formatPlatform(invite.platform)}{' '}
                      account:{' '}
                      <Typography
                        variant="body"
                        size="medium"
                        as="strong"
                        decoration={{ bold: true, underline: true }}
                      >
                        {invite.username}
                      </Typography>
                    </Typography>
                  </li>
                )}
              </ul>
            </div>

            {/* ── CTAs ──────────────────────────────────────────────────────────── */}
            <div className="mt-6 flex flex-col gap-3">
              <Button fullWidth size="large" onClick={handleAccept} isLoading={isAccepting}>
                Interested? Learn more
              </Button>
              <Button fullWidth size="large" variant="secondary" onClick={handleNoThanks}>
                No thanks
              </Button>
            </div>
          </div>
        </ScreenCard>

        <Modal
          isOpen={showDeclineConfirmation}
          onClose={() => setShowDeclineConfirmation(false)}
          icon="circle-info-filled"
          title="Decline this partnership?"
          description="If you decline, this invitation will be removed and you won't receive this product. This action can't be undone."
          primaryButton={{
            label: 'Decline invitation',
            onClick: handleConfirmDecline,
            isLoading: declineMutation.isPending,
          }}
          secondaryButton={{
            label: 'Cancel',
            onClick: () => setShowDeclineConfirmation(false),
            disabled: declineMutation.isPending,
          }}
        />
      </>
    </PageColumn>
  );
}

export default MagicLink;
